import { format, parseISO } from 'date-fns';

/**
 * キャスト専用オーダー画面で扱うオーダーの表示用フィールドをまとめた型。
 * バックエンドの生データ（getOrders / getOrder のレスポンス）は [key: string]: any の
 * 緩い型のため、画面側で必要なフィールドだけを安全に取り出すためのヘルパーをここに集約する。
 */
export interface CastOrderView {
  id: string | number;
  reservationSortKey: number; // 予約日時のソート用（ミリ秒。無効なら Number.MAX_SAFE_INTEGER）
  reservationLabel: string; // 予約時間（表示用）
  orderTypeLabel: string;   // 予約タイプ（表示用）
  courseLabel: string;      // コース（表示用）
  hotelLabel: string;       // ホテル（表示用）
  status?: string;
  isCompleted: boolean;     // 完了オーダーか（入室時間が入力済み or ステータスが完了）
  entryTimeLabel: string | null; // 入室時間（HH:mm）
  exitTimeLabel: string | null;  // 退出時間（入室時間 + コース時間で自動計算、HH:mm）
  castbackLabel: string | null;  // キャストバック金額（表示用）
  castNameLabel: string;         // 担当キャスト名（表示用）
}

/**
 * 予約タイプ(order_type)を日本語ラベルに変換する。
 */
export const getOrderTypeLabel = (orderType?: string): string => {
  switch (orderType) {
    case 'nomination':
      return '指名';
    case 'direct_nomination':
      return '本指名';
    case 'free':
      return 'フリー';
    default:
      return orderType || 'フリー';
  }
};

/**
 * 予約日時を「MM/dd HH:mm」形式で表示する。
 * reservation_datetime(ISO) を優先し、無ければ reservation_date + reservation_time や
 * pickup_date/pickup_time から組み立てる。パースできない場合は元の文字列を返す。
 */
export const getReservationLabel = (order: any): string => {
  const iso: string | undefined =
    order?.reservation_datetime || order?.reservation_date_time || order?.scheduled_at;
  if (iso) {
    try {
      return format(parseISO(iso), 'MM/dd HH:mm');
    } catch {
      // パース失敗時は後続のフォールバックへ
    }
  }

  // 日付 + 時刻を個別フィールドから組み立てる
  const date: string = order?.reservation_date || order?.pickup_date || '';
  const time: string = order?.reservation_time || order?.pickup_time || order?.entry_time || '';
  const combined = `${date} ${time}`.trim();
  return combined || '未設定';
};

/**
 * コース名を表示用に取得する。コースは course_duration(分) で識別されるため、
 * 分数表示（例: 60分）を優先する。course_name はマスタ名（例「新しいコース」）で
 * キャストには分かりにくいため使わない。
 */
export const getCourseLabel = (order: any): string => {
  if (order?.course_duration) return `${order.course_duration}分`;
  if (typeof order?.course === 'number') return `${order.course}分`;
  if (order?.course && /^\d+$/.test(String(order.course))) return `${order.course}分`;
  if (order?.course_name) return order.course_name;
  return '未設定';
};

/**
 * ホテル名を表示用に取得する。hotel_name を優先し、無ければ delivery_address を使う。
 */
export const getHotelLabel = (order: any): string => {
  return order?.hotel_name || order?.delivery_address || order?.hotel_address || '未設定';
};

/**
 * 入室時間(HH:mm)を取得する。entry_time_value(HH:MM)を優先し、
 * 無ければ entry_time(ISO)から抽出する。無ければ null。
 */
export const getEntryTimeLabel = (order: any): string | null => {
  if (order?.entry_time_value) return order.entry_time_value;
  if (order?.entry_time) {
    try {
      return format(parseISO(order.entry_time), 'HH:mm');
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * コース時間（分）を取得する。
 */
const getCourseMinutes = (order: any): number | null => {
  if (order?.course_duration) return Number(order.course_duration);
  if (typeof order?.course === 'number') return order.course;
  if (order?.course && /^\d+$/.test(String(order.course))) return Number(order.course);
  return null;
};

/**
 * 退出時間を自動計算する。入室時間(ISO or HH:mm) + コース時間(分) + サービスタイム(分)。
 * 入室時間かコース時間が無ければ null。日付跨ぎにも対応する。
 */
export const getExitTimeLabel = (order: any): string | null => {
  const courseMinutes = getCourseMinutes(order);
  if (!courseMinutes) return null;

  // サービスタイム（追加時間・分）も退出時間に加算する
  const serviceMinutes = Number(order?.service_time) || 0;
  const minutes = courseMinutes + serviceMinutes;

  // 入室時刻の Date を作る。entry_time(ISO) があればそれを基準に、
  // 無ければ entry_time_value(HH:mm) を本日の時刻として扱う。
  let base: Date | null = null;
  if (order?.entry_time) {
    try {
      base = parseISO(order.entry_time);
    } catch {
      base = null;
    }
  }
  if (!base && order?.entry_time_value) {
    const m = String(order.entry_time_value).match(/^(\d{1,2}):(\d{2})/);
    if (m) {
      base = new Date();
      base.setHours(Number(m[1]), Number(m[2]), 0, 0);
    }
  }
  if (!base || isNaN(base.getTime())) return null;

  const exit = new Date(base.getTime() + minutes * 60 * 1000);
  return format(exit, 'HH:mm');
};

/**
 * キャストバック金額を表示用（¥区切り）に取得する。無ければ null。
 */
export const getCastbackLabel = (order: any): string | null => {
  const value = order?.castback;
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (isNaN(num)) return null;
  return `¥${num.toLocaleString()}`;
};

/**
 * 完了オーダーかどうかを判定する。
 * - ステータスが明示的にキャンセル/チェンジの場合は、入室時間があっても完了にしない。
 * - ステータスが完了なら完了。
 * - それ以外は入室時間が入力済みなら完了扱い。
 */
export const isCompletedOrder = (order: any): boolean => {
  const status = order?.status;
  // キャンセル・チェンジは入室済みでも完了にしない
  if (status === 'キャンセル' || status === 'cancelled' || status === 'チェンジ' || status === 'changed') {
    return false;
  }
  if (status === '完了' || status === 'completed') return true;
  return !!getEntryTimeLabel(order);
};

/**
 * 担当キャスト名を表示用に取得する。バックエンドは cast_name を「, 」区切りの文字列で返す。
 */
export const getCastNameLabel = (order: any): string => {
  const raw = order?.cast_name;
  if (Array.isArray(raw)) return raw.filter(Boolean).join('、') || '未割当';
  if (typeof raw === 'string' && raw.trim() !== '') return raw;
  return '未割当';
};

/**
 * 予約日時のソート用キー（ミリ秒）を返す。
 * reservation_datetime(ISO) を優先し、無ければ日付+時刻から組み立てる。
 * 取得できない場合は末尾に送るため Number.MAX_SAFE_INTEGER を返す。
 */
export const getReservationSortKey = (order: any): number => {
  const iso: string | undefined =
    order?.reservation_datetime || order?.reservation_date_time || order?.scheduled_at;
  if (iso) {
    try {
      const t = parseISO(iso).getTime();
      if (!isNaN(t)) return t;
    } catch {
      // フォールバックへ
    }
  }
  const date: string = order?.reservation_date || order?.pickup_date || '';
  const time: string = order?.reservation_time || order?.pickup_time || '';
  if (date) {
    const t = new Date(`${date}T${(time || '00:00')}`).getTime();
    if (!isNaN(t)) return t;
  }
  return Number.MAX_SAFE_INTEGER;
};

/**
 * 生のオーダーデータを一覧表示用の CastOrderView に変換する。
 */
export const toCastOrderView = (order: any): CastOrderView => ({
  id: order?.id,
  reservationSortKey: getReservationSortKey(order),
  reservationLabel: getReservationLabel(order),
  orderTypeLabel: getOrderTypeLabel(order?.order_type),
  courseLabel: getCourseLabel(order),
  hotelLabel: getHotelLabel(order),
  status: order?.status,
  isCompleted: isCompletedOrder(order),
  entryTimeLabel: getEntryTimeLabel(order),
  exitTimeLabel: getExitTimeLabel(order),
  castbackLabel: getCastbackLabel(order),
  castNameLabel: getCastNameLabel(order),
});

/**
 * option_details を安全に配列へ正規化する。
 * バックエンドのJSONカラムは通常オブジェクトを返すが、過去データが JSON文字列として
 * 保存されているケースがあるため、文字列ならパースする。dict以外の要素は除外する。
 */
export const normalizeOptionDetails = (
  raw: any
): { id?: number | string; name: string; castback: number; profit: number }[] => {
  let details = raw;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(details)) return [];
  return details
    .filter((d) => d && typeof d === 'object')
    .map((d) => ({
      id: d.id,
      name: d.name || '',
      castback: Number(d.castback) || 0,
      profit: Number(d.profit) || 0,
    }));
};

/**
 * getOrders のレスポンス（{orders:[]} または配列）からオーダー配列を取り出す。
 */
export const extractOrders = (response: any): any[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.orders)) return response.orders;
  return [];
};
